import { useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  CheckSquare,
  Edit2,
  ExternalLink,
  Image as ImageIcon,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import type { ListMediaQuery, MediaAdminDetail, MediaAdminItem } from '@news-cms/shared';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ImageUploadDialog } from '@/components/posts/image-upload-dialog';
import { useSession } from '@/lib/auth';
import { hasPermission } from '@/lib/permissions';
import {
  mediaAbsoluteUrl,
  mediaDetailQueryOptions,
  mediaListQueryOptions,
  useBulkDeleteMedia,
  useDeleteMedia,
  useUpdateMedia,
} from '@/lib/media';

interface MediaSearch {
  page?: number;
  filter?: 'all' | 'unused';
  q?: string;
}

export const Route = createFileRoute('/_authenticated/media/')({
  validateSearch: (search: Record<string, unknown>): MediaSearch => ({
    page: typeof search.page === 'number' ? search.page : undefined,
    filter: search.filter === 'unused' ? 'unused' : search.filter === 'all' ? 'all' : undefined,
    q: typeof search.q === 'string' ? search.q : undefined,
  }),
  component: MediaPage,
});

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function MediaPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { user } = useSession();
  const filter: Partial<ListMediaQuery> = {
    page: search.page ?? 1,
    perPage: 24,
    filter: search.filter ?? 'all',
    q: search.q,
  };
  const query = useQuery(mediaListQueryOptions(filter));
  const queryClient = useQueryClient();
  const deleteMutation = useDeleteMedia();
  const bulkDeleteMutation = useBulkDeleteMedia();

  const [openId, setOpenId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<MediaAdminItem | null>(null);
  const [searchInput, setSearchInput] = useState(filter.q ?? '');
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [pendingBulkDelete, setPendingBulkDelete] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);

  const canUpload = hasPermission(user, 'media.upload');
  const canDelete = hasPermission(user, 'media.delete');
  const canUpdate = hasPermission(user, 'media.update');
  const canReadAudit = hasPermission(user, 'audit.read');
  const items = query.data?.items ?? [];
  const total = query.data?.pagination.total ?? 0;
  const totalBytes = query.data?.totalBytes ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / 24));
  const currentPage = filter.page ?? 1;

  const setFilter = (value: 'all' | 'unused') =>
    void navigate({ search: { ...search, filter: value, page: 1 } });
  const goPage = (page: number) => void navigate({ search: { ...search, page } });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    void navigate({ search: { ...search, q: searchInput.trim() || undefined, page: 1 } });
  };

  const handleConfirmDelete = () => {
    if (!pendingDelete) return;
    deleteMutation.mutate(pendingDelete.id, {
      onSuccess: () => {
        setPendingDelete(null);
        if (openId === pendingDelete.id) setOpenId(null);
      },
      onError: () => setPendingDelete(null),
    });
  };

  // Selection helpers — Set<string> + per-id toggle. Selection
  // survives filter/page changes (intentional — moving across
  // pages to gather more "unused" candidates is the realistic
  // cleanup gesture). Cleared explicitly via the X button.
  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const selectAllOnPage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const m of items) next.add(m.id);
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const handleConfirmBulkDelete = () => {
    if (selected.size === 0) return;
    bulkDeleteMutation.mutate(Array.from(selected), {
      onSuccess: () => {
        clearSelection();
        setPendingBulkDelete(false);
      },
      onError: () => setPendingBulkDelete(false),
    });
  };

  return (
    <div className="p-4 sm:p-8">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-50">
              Media Library
            </h1>
            {/* "X ไฟล์ · Y MB" reflects the CURRENT filter, so the
                "ไม่ได้ใช้" view answers "how much R2 do I reclaim
                if I bulk-delete this set?" directly. */}
            <p className="mt-1 text-sm text-slate-500">
              {total > 0 ? `${total} ไฟล์ · ${formatBytes(totalBytes)}` : 'ยังไม่มีสื่อ'}
            </p>
          </div>
          {canUpload && (
            <Button type="button" onClick={() => setUploadOpen(true)}>
              <Upload className="mr-2 h-4 w-4" />
              อัปโหลด
            </Button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setFilter('all')}
            className={
              (search.filter ?? 'all') === 'all'
                ? 'bg-brand-600 rounded-full px-3 py-1 text-xs font-medium text-white'
                : 'rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300'
            }
          >
            ทั้งหมด
          </button>
          <button
            type="button"
            onClick={() => setFilter('unused')}
            className={
              search.filter === 'unused'
                ? 'bg-brand-600 rounded-full px-3 py-1 text-xs font-medium text-white'
                : 'rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300'
            }
          >
            ไม่ได้ใช้
          </button>
          {/* Master clear — parity with /posts + /audit + /comments
              + /users. Resets filter + q + local searchInput. */}
          {((search.filter && search.filter !== 'all') || search.q) && (
            <button
              type="button"
              onClick={() => {
                setSearchInput('');
                void navigate({ search: {} });
              }}
              className="ml-1 text-xs text-slate-500 hover:underline"
              title="ล้างทุกตัวกรอง"
            >
              ล้างทั้งหมด
            </button>
          )}
        </div>

        <form onSubmit={handleSearch} className="flex gap-2">
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="ค้นหาตามชื่อไฟล์ / alt text..."
            className="max-w-md"
          />
          <Button type="submit" variant="outline">
            ค้นหา
          </Button>
          {search.q && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setSearchInput('');
                void navigate({ search: { ...search, q: undefined, page: 1 } });
              }}
            >
              ล้าง
            </Button>
          )}
        </form>

        {/* Bulk action bar — appears when anything is selected. Persistent
            across pagination so users can pick a few from each page of
            the "ไม่ได้ใช้" filter before triggering the bulk delete. */}
        {selected.size > 0 && canDelete && (
          <div className="bg-brand-50 dark:bg-brand-900/30 flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 p-3 dark:border-slate-800">
            <span className="text-sm font-medium">เลือก {selected.size} ไฟล์</span>
            {items.length > 0 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={selectAllOnPage}
                disabled={bulkDeleteMutation.isPending}
              >
                <CheckSquare className="mr-1.5 h-3.5 w-3.5" />
                เลือกทั้งหน้านี้
              </Button>
            )}
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={() => setPendingBulkDelete(true)}
              disabled={bulkDeleteMutation.isPending}
            >
              <Trash2 className="mr-1.5 h-3.5 w-3.5" />
              ลบที่เลือก
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={clearSelection}
              disabled={bulkDeleteMutation.isPending}
              className="ml-auto"
            >
              <X className="mr-1.5 h-3.5 w-3.5" />
              ล้างเลือก
            </Button>
          </div>
        )}

        {query.isLoading ? (
          <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-500 dark:border-slate-800 dark:bg-slate-900">
            กำลังโหลด...
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-lg border border-slate-200 bg-white p-12 text-center dark:border-slate-800 dark:bg-slate-900">
            <ImageIcon className="h-12 w-12 text-slate-300" />
            <p className="text-sm text-slate-500">
              {search.filter === 'unused' ? 'ไม่มีสื่อที่ไม่ได้ใช้' : 'ยังไม่มีสื่อ'}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
              {items.map((m) => {
                const isSelected = selected.has(m.id);
                return (
                  <div
                    key={m.id}
                    className={`group relative aspect-square overflow-hidden rounded-md border bg-slate-100 dark:bg-slate-800 ${
                      isSelected
                        ? 'border-brand-500 ring-brand-500 ring-2'
                        : 'border-slate-200 dark:border-slate-800'
                    }`}
                  >
                    {/* Card body = open detail. Wrapped in a button for
                        keyboard nav; the checkbox overlay sits above and
                        absorbs its own click via stopPropagation so it
                        doesn't fall through to the detail dialog. */}
                    <button type="button" onClick={() => setOpenId(m.id)} className="h-full w-full">
                      <img
                        src={mediaAbsoluteUrl(m.url)}
                        alt={m.altText ?? m.originalName}
                        loading="lazy"
                        className="h-full w-full object-cover transition group-hover:scale-105"
                      />
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 text-left">
                        <div className="truncate text-[10px] text-white">{m.originalName}</div>
                        <div className="text-[10px] text-white/70">
                          {formatBytes(m.size)} · {m.usageCount} use
                        </div>
                      </div>
                    </button>
                    {/* "ใหม่" pill — top-right so the checkbox (top-left)
                        and the badge don't fight for the same corner.
                        Parity with posts/comments/users/taxonomies. */}
                    {Date.now() - new Date(m.createdAt).getTime() < 24 * 60 * 60 * 1000 && (
                      <span className="absolute right-1.5 top-1.5 rounded-full bg-emerald-500 px-1.5 py-0.5 text-[9px] font-semibold text-white shadow-sm">
                        ใหม่
                      </span>
                    )}
                    {canDelete && (
                      <label
                        className="absolute left-1.5 top-1.5 flex h-6 w-6 cursor-pointer items-center justify-center rounded bg-white/90 shadow-sm dark:bg-slate-900/90"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(m.id)}
                          aria-label={`เลือก ${m.originalName}`}
                          className="h-4 w-4"
                        />
                      </label>
                    )}
                  </div>
                );
              })}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between text-sm">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage <= 1}
                  onClick={() => goPage(currentPage - 1)}
                >
                  ก่อนหน้า
                </Button>
                <span className="text-slate-500">
                  หน้า {currentPage} จาก {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage >= totalPages}
                  onClick={() => goPage(currentPage + 1)}
                >
                  ถัดไป
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      <MediaDetailDialog
        openId={openId}
        onOpenChange={(open) => {
          if (!open) setOpenId(null);
        }}
        onDelete={(item) => setPendingDelete(item)}
        canDelete={canDelete}
        canUpdate={canUpdate}
        canReadAudit={canReadAudit}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
        title="ยืนยันการลบสื่อ"
        description={
          pendingDelete
            ? `จะลบ "${pendingDelete.originalName}" (${formatBytes(pendingDelete.size)}) — ลบทั้ง R2 + DB. การลบไม่สามารถกู้คืน. ถ้าสื่อถูกใช้ในบทความ ระบบจะปฏิเสธ.`
            : undefined
        }
        confirmLabel="ลบ"
        variant="destructive"
        onConfirm={handleConfirmDelete}
        isLoading={deleteMutation.isPending}
      />

      <ConfirmDialog
        open={pendingBulkDelete}
        onOpenChange={(open) => {
          if (!open) setPendingBulkDelete(false);
        }}
        title={`ลบ ${selected.size} ไฟล์ — กู้คืนไม่ได้`}
        description={`จะลบทั้ง R2 + DB. ไฟล์ที่ยังถูกใช้ในบทความจะถูกข้าม (จะแสดงสรุปหลังเสร็จ).`}
        confirmLabel="ลบที่เลือก"
        variant="destructive"
        onConfirm={handleConfirmBulkDelete}
        isLoading={bulkDeleteMutation.isPending}
      />

      <ImageUploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        libraryMode
        onInsert={() => {
          // Library doesn't "insert anywhere" — just refresh the grid.
          void queryClient.invalidateQueries({ queryKey: ['media', 'list'] });
        }}
      />
    </div>
  );
}

interface DetailProps {
  openId: string | null;
  onOpenChange: (open: boolean) => void;
  onDelete: (item: MediaAdminItem) => void;
  canDelete: boolean;
  canUpdate: boolean;
  canReadAudit: boolean;
}

function MediaDetailDialog({
  openId,
  onOpenChange,
  onDelete,
  canDelete,
  canUpdate,
  canReadAudit,
}: DetailProps) {
  const query = useQuery({
    ...mediaDetailQueryOptions(openId ?? ''),
    enabled: openId !== null,
  });
  const m = query.data;

  return (
    <Dialog open={openId !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{m?.originalName ?? 'กำลังโหลด...'}</DialogTitle>
          <DialogDescription>
            {m
              ? `${m.mimeType} · ${formatBytes(m.size)}${m.width && m.height ? ` · ${m.width}×${m.height}` : ''}`
              : ''}
          </DialogDescription>
        </DialogHeader>

        {query.isLoading && <p className="text-sm text-slate-500">กำลังโหลดรายละเอียด...</p>}

        {m && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div className="md:col-span-2">
              <img
                src={mediaAbsoluteUrl(m.url)}
                alt={m.altText ?? m.originalName}
                className="max-h-96 w-full rounded-md border border-slate-200 object-contain dark:border-slate-800"
              />
            </div>
            <div className="space-y-3 text-sm">
              <div>
                <div className="text-[11px] uppercase text-slate-400">URL</div>
                <a
                  href={mediaAbsoluteUrl(m.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="break-all font-mono text-[11px] text-blue-600 hover:underline dark:text-blue-400"
                >
                  {m.url}
                </a>
              </div>
              <MediaMetadataEditor key={m.id} media={m} canUpdate={canUpdate} />
              <div>
                <div className="text-[11px] uppercase text-slate-400">อัปโหลดโดย</div>
                <p className="text-slate-700 dark:text-slate-300">{m.uploaderName}</p>
                <p className="text-[11px] text-slate-500">
                  {new Date(m.createdAt).toLocaleString('th-TH')}
                </p>
              </div>
              <div>
                <div className="text-[11px] uppercase text-slate-400">SHA-256</div>
                <p className="break-all font-mono text-[10px] text-slate-500">{m.hash}</p>
              </div>

              <div>
                <div className="mb-1.5 text-[11px] uppercase text-slate-400">
                  ใช้ในบทความ ({m.usedBy.length})
                </div>
                {m.usedBy.length === 0 ? (
                  <p className="text-xs italic text-slate-400">ยังไม่ได้ใช้ — ลบได้</p>
                ) : (
                  <ul className="space-y-1">
                    {m.usedBy.map((u) => (
                      <li key={`${u.postId}-${u.usage}`} className="text-xs">
                        <Link
                          to="/posts/$postId"
                          params={{ postId: u.postId }}
                          className="flex items-center gap-1 text-blue-600 hover:underline dark:text-blue-400"
                          onClick={() => onOpenChange(false)}
                        >
                          <ExternalLink className="h-3 w-3" />
                          {u.postTitle}
                        </Link>
                        <span className="ml-4 text-[10px] text-slate-500">
                          ({u.usage} · {u.postStatus})
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {canDelete && (
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  className="w-full"
                  onClick={() => onDelete(m)}
                  disabled={m.usedBy.length > 0}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {m.usedBy.length > 0 ? 'ลบไม่ได้ — ใช้อยู่' : 'ลบสื่อนี้'}
                </Button>
              )}
              {!canDelete && (
                <p className="flex items-center gap-1 text-[11px] text-slate-500">
                  <X className="h-3 w-3" /> ไม่มีสิทธิ์ลบ
                </p>
              )}
              {/* Audit drill — parity with /users + /posts +
                  /webhooks + /feeds. Lives inside the dialog rather
                  than on the grid tile so the tile stays clean and
                  focused on visual scan. */}
              {canReadAudit && (
                <Link
                  to="/audit"
                  search={{ resourceType: 'media', resourceId: m.id }}
                  className="flex items-center justify-center gap-1.5 text-xs text-blue-600 hover:underline dark:text-blue-400"
                >
                  <Activity className="h-3.5 w-3.5" />
                  ดู audit ของสื่อนี้
                </Link>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Editable alt-text + caption block. Read-only by default with a small
 * "แก้ไข" link; clicking switches to inputs + save/cancel. Optimistic
 * cancel restores the prior values from `media` props (which mutate
 * via the parent query invalidation on success).
 */
function MediaMetadataEditor({
  media,
  canUpdate,
}: {
  media: MediaAdminDetail;
  canUpdate: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [altDraft, setAltDraft] = useState(media.altText ?? '');
  const [captionDraft, setCaptionDraft] = useState(media.caption ?? '');
  const update = useUpdateMedia();

  const startEdit = () => {
    setAltDraft(media.altText ?? '');
    setCaptionDraft(media.caption ?? '');
    setEditing(true);
  };

  const save = () => {
    const nextAlt = altDraft.trim();
    const nextCaption = captionDraft.trim();
    update.mutate(
      {
        id: media.id,
        // Send null (not empty string) to clear — matches the schema's
        // explicit "use null to clear" convention.
        altText: nextAlt === '' ? null : nextAlt,
        caption: nextCaption === '' ? null : nextCaption,
      },
      { onSuccess: () => setEditing(false) },
    );
  };

  if (!editing) {
    return (
      <div className="space-y-3">
        <div>
          <div className="flex items-center justify-between gap-2">
            <div className="text-[11px] uppercase text-slate-400">Alt text</div>
            {canUpdate && (
              <button
                type="button"
                onClick={startEdit}
                className="text-brand-600 hover:text-brand-700 dark:text-brand-400 inline-flex items-center gap-0.5 text-[11px] hover:underline"
              >
                <Edit2 className="h-2.5 w-2.5" />
                แก้ไข
              </button>
            )}
          </div>
          <p className="text-slate-700 dark:text-slate-300">
            {media.altText ?? <em className="text-slate-400">— ยังไม่ระบุ</em>}
          </p>
        </div>
        {media.caption && (
          <div>
            <div className="text-[11px] uppercase text-slate-400">Caption</div>
            <p className="text-slate-700 dark:text-slate-300">{media.caption}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2 rounded-md border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-800/40">
      <div>
        <label className="text-[11px] uppercase text-slate-400" htmlFor={`alt-${media.id}`}>
          Alt text
        </label>
        <Input
          id={`alt-${media.id}`}
          value={altDraft}
          onChange={(e) => setAltDraft(e.target.value)}
          placeholder="คำอธิบายรูปสำหรับผู้พิการทางสายตา"
          maxLength={500}
        />
      </div>
      <div>
        <label className="text-[11px] uppercase text-slate-400" htmlFor={`cap-${media.id}`}>
          Caption
        </label>
        <Textarea
          id={`cap-${media.id}`}
          value={captionDraft}
          onChange={(e) => setCaptionDraft(e.target.value)}
          placeholder="ข้อความคำบรรยายใต้รูป (optional)"
          rows={2}
          maxLength={1000}
        />
      </div>
      <div className="flex gap-1.5">
        <Button type="button" size="sm" onClick={save} disabled={update.isPending}>
          {update.isPending ? 'กำลังบันทึก...' : 'บันทึก'}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setEditing(false)}
          disabled={update.isPending}
        >
          ยกเลิก
        </Button>
      </div>
    </div>
  );
}
